import React, { Fragment } from 'react';

import { buildUpgradeUrl } from '@zapier/checkout';
import { useLink } from '@zapier/common-hooks';
import { Price } from '@zapier/currency';
import { Button, Link } from '@zapier/design-system';

import { Currency, Interval, Plan, TrialType } from '@zapier/upsells/src/types';

import { UpgradeButton } from '@zapier/upsells/src/components/UpgradeModal/UpgradeModalLayout/UpgradeNudge/UpgradeButton';

const monthlyAmount = (amount: number, interval: Interval) =>
  interval === 'year' ? amount / 12 : amount;

const HeldTasksButton = ({ onClick }: { onClick?: () => void }) => {
  const { routeTo } = useLink();

  const handleClick = () => {
    if (onClick) {
      onClick();
    }

    if (routeTo) {
      routeTo('/app/zaps/folder/held');
    }
  };

  return (
    <Button color="secondary" key="see-held-tasks" onClick={handleClick}>
      See held tasks
    </Button>
  );
};

const TaskLimitUpgradeButton = ({
  currentPlanInterval,
  currentPlanName,
  destinationPlanName,
  onClick,
}: {
  currentPlanInterval: Interval;
  currentPlanName: string;
  destinationPlanName: string;
  onClick?: () => void;
}) => {
  const { routeTo } = useLink();

  const handleClick = () => {
    if (onClick) {
      onClick();
    }

    if (routeTo) {
      const url = buildUpgradeUrl(
        currentPlanName,
        destinationPlanName,
        currentPlanInterval
      );

      if (url) {
        routeTo(url);
      }
    }
  };

  return <UpgradeButton onClick={handleClick} />;
};

const TaskHistoryButton = () => (
  <Button color="secondary" href="/app/history/usage" key="see-task-history">
    See task history
  </Button>
);

const baseBodyText = (
  <Fragment>
    You've run out of available tasks on your account, and{' '}
    <Link href="/help/manage/tasks/learn-about-tasks-in-zapier" target="_blank">
      your Tasks are held
    </Link>
    .
  </Fragment>
);

type ActiveViewProps = {
  currency: Currency;
  currentPlan: Plan;
  destinationPlan: Plan;
};

/**
 * Note: Upgrade buttons in these views take the user to the upgrade
 * page rather than doing the upgrade inline because these nudges could
 * be used in `web-ssr`, and the payment app doesn't work in `web-ssr`.
 * This also gives the user the chance to change their billing interval,
 * which is common for annual users who need to upgrade to the next
 * task limit just to get them through the rest of the month.
 */
const views = {
  ['LEAVING_FREE']: ({
    currency,
    currentPlan,
    destinationPlan,
  }: ActiveViewProps) => ({
    bodyText: (
      <Fragment>
        All{' '}
        <Link href="/help/manage/history/learn-about-tasks-in-zapier">
          tasks from your Zaps
        </Link>{' '}
        will be held until you upgrade your plan or your monthly usage resets.{' '}
        <strong>
          Get access to {Number(destinationPlan.tasks).toLocaleString()} tasks
          with the {destinationPlan.type} plan for{' '}
          <Price currency={currency}>
            {monthlyAmount(destinationPlan.amount, destinationPlan.interval)}
          </Price>{' '}
          per month.
        </strong>
      </Fragment>
    ),

    buttons: [
      <TaskHistoryButton />,
      <TaskLimitUpgradeButton
        currentPlanInterval={currentPlan.interval}
        currentPlanName={currentPlan.name}
        destinationPlanName={destinationPlan.name}
      />,
    ],

    type: 'LEAVING_FREE',
  }),

  ['NEW_PLAN_TYPE']: ({
    currency,
    currentPlan,
    destinationPlan,
  }: ActiveViewProps) => ({
    bodyText: currentPlan ? (
      <Fragment>
        {baseBodyText} Upgrade to a Zapier {destinationPlan.type} account now to
        get {Number(destinationPlan.tasks).toLocaleString()} monthly tasks for{' '}
        <Price currency={currency}>
          {monthlyAmount(destinationPlan.amount, destinationPlan.interval) -
            monthlyAmount(currentPlan.amount, currentPlan.interval)}
        </Price>{' '}
        more per month.
      </Fragment>
    ) : undefined,

    buttons: [
      <HeldTasksButton />,
      <TaskLimitUpgradeButton
        currentPlanInterval={currentPlan.interval}
        currentPlanName={currentPlan.name}
        destinationPlanName={destinationPlan.name}
      />,
    ],

    type: 'NEW_PLAN_TYPE',
  }),

  ['NEW_TASK_LIMIT_SAME_PLAN_TYPE']: ({
    currency,
    currentPlan,
    destinationPlan,
  }: ActiveViewProps) => ({
    bodyText: (
      <Fragment>
        {baseBodyText} Upgrade your plan now to get{' '}
        {Number(destinationPlan.tasks).toLocaleString()} monthly tasks for{' '}
        <Price currency={currency}>
          {monthlyAmount(destinationPlan.amount, destinationPlan.interval) -
            monthlyAmount(currentPlan.amount, currentPlan.interval)}
        </Price>{' '}
        more per month.
      </Fragment>
    ),

    buttons: [
      <HeldTasksButton />,
      <TaskLimitUpgradeButton
        currentPlanInterval={currentPlan.interval}
        currentPlanName={currentPlan.name}
        destinationPlanName={destinationPlan.name}
      />,
    ],

    type: 'NEW_TASK_LIMIT_SAME_PLAN_TYPE',
  }),
};

type Props = {
  activeTrialType: TrialType | null | undefined;
  currency: Currency;
  currentPlan: Plan;
  destinationPlan: Plan | null | undefined;
};

export const getActiveView = ({
  activeTrialType,
  currency,
  currentPlan,
  destinationPlan,
}: Props) => {
  if (activeTrialType === 'Team' || activeTrialType === 'Company') {
    return views.NEW_PLAN_TYPE({
      currency,
      currentPlan,
      destinationPlan: destinationPlan!,
    });
  }

  if (activeTrialType === 'Professional' || currentPlan.amount === 0) {
    return views.LEAVING_FREE({
      currency,
      currentPlan,
      destinationPlan: destinationPlan!,
    });
  }

  if (currentPlan.type === destinationPlan?.type) {
    return views.NEW_TASK_LIMIT_SAME_PLAN_TYPE({
      currency,
      currentPlan,
      destinationPlan: destinationPlan!,
    });
  }

  return views.NEW_PLAN_TYPE({
    currency,
    currentPlan,
    destinationPlan: destinationPlan!,
  });
};
